// Exportable "share with staff" schedule preview.
// Layout: Variant A (decorated / cards). Green = outreach, Blue = travel.
// Header shows the month in the top-right corner (staff name is inside the
// PNG body). No footer.
//
// Renders a live HTML preview inside a dialog with staff + month controls,
// then serializes the node to PNG using html-to-image so it can be shared
// with each team member.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Download, Plane, CoffeeIcon, MapPin, Wrench, CalendarDays } from "lucide-react";
import { toSvg } from "html-to-image";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { api } from "@/lib/consult/api";

const GREEN = "#2E7D5B";
const GREEN_SOFT = "#E4EDE8";
const BLUE = "#1F4E79";
const BLUE_SOFT = "#DDE7F1";
const RED = "#B4351B";
const RED_SOFT = "#F3D9D3";
const AMBER = "#C77D26";
const SLATE = "#1F2937";

// Zero-padded 12-hour clock — matches the main OutreachPanel so single-
// and double-digit hours align vertically ("09:00 AM – 05:00 PM").
const prettyTime = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const BG = "#F4F3EF";

// Per-(staff, month) fetch cache — flipping between already-viewed months is
// instant and never collapses the day list (which made the dialog jump).
const PREVIEW_CACHE = new Map();

const STAFF = [
  { full: "George Holness", short: "George" },
  { full: "Malachi Whyte", short: "Malachi" },
  { full: "Racquel Anthony", short: "Racquel" },
  { full: "Manitoba", short: "Manitoba" },
  { full: "Christian Marcoux", short: "Christian" },
];

const monthShift = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromYMD = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Build 6 rows × 7 columns starting from the Sunday on-or-before the 1st.
function buildCalendarGrid(ym) {
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay()); // back to Sunday
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({
      date: d,
      ymd: toYMD(d),
      inMonth: d.getMonth() === m - 1,
    });
  }
  return cells;
}

export default function SchedulePreviewDialog({ open, onOpenChange, defaultStaff, defaultMonth, workTypes = [], staffList = null, lockStaff = null }) {
  const [staff, setStaff] = useState(defaultStaff || STAFF[0].full);
  // Staff-scoped viewers are pinned to their own schedule.
  useEffect(() => { if (lockStaff && staff !== lockStaff) setStaff(lockStaff); }, [lockStaff, staff]);
  const [month, setMonth] = useState(() => {
    if (defaultMonth) return defaultMonth;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    if (defaultStaff) setStaff(defaultStaff);
  }, [defaultStaff]);

  // Sync the visible month with whichever month the parent Outreach panel is
  // currently viewing, but only when the dialog (re)opens. Once open, the
  // user's own month-arrow clicks take precedence.
  useEffect(() => {
    if (open && defaultMonth) setMonth(defaultMonth);
  }, [open, defaultMonth]);

  const load = useCallback(async () => {
    if (!open) return;
    // Cache each (staff, month) fetch so flipping back to a month renders it
    // INSTANTLY — without this, the day list collapses to nothing while the
    // new month loads and the whole dialog (and the month arrows under your
    // thumb) bounces.
    const cacheKey = `${staff}|${month}`;
    const cached = PREVIEW_CACHE.get(cacheKey);
    if (cached) setEntries(cached);
    setLoading(true);
    try {
      const [y, m] = month.split("-").map(Number);
      // Fetch a ±7-day buffer so the mini-calendar's previous/next-month
      // spillover days (e.g. Aug 30/31 in the September grid) still show
      // their real category colour instead of appearing empty.
      const buf = 7;
      const fromD = new Date(y, m - 1, 1);
      fromD.setDate(fromD.getDate() - buf);
      const toD = new Date(y, m, 0);
      toD.setDate(toD.getDate() + buf);
      const { data } = await api.get("/outreach", {
        params: { staff, from: toYMD(fromD), to: toYMD(toD) },
      });
      PREVIEW_CACHE.set(cacheKey, data || []);
      setEntries(data || []);
    } catch {
      toast.error("Failed to load schedule.");
    } finally {
      setLoading(false);
    }
  }, [open, staff, month]);

  useEffect(() => { load(); }, [load]);

  // Load the (staff, month) completion flag — when true, we replace the
  // city column with total hours worked per day in the shareable output.
  const [monthCompleted, setMonthCompleted] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/outreach-status", {
          params: { staff, month },
        });
        if (!cancelled) setMonthCompleted(Boolean(data?.completed));
      } catch { if (!cancelled) setMonthCompleted(false); }
    })();
    return () => { cancelled = true; };
  }, [open, staff, month]);

  const monthLabel = useMemo(
    () => format(new Date(`${month}-01T00:00:00`), "MMMM yyyy"),
    [month],
  );
  const monthLabelShort = useMemo(
    () => format(new Date(`${month}-01T00:00:00`), "MMM yyyy"),
    [month],
  );

  // Bucketed arrays. The mini-calendar and its coloured "day pills" use
  // the full ±7-day fetch (so spillover days from the previous/next month
  // still show their real colour). The legend counts and the exported
  // date lists below are filtered down to just the viewed month.
  const outreachAll = useMemo(
    () => entries
      .filter((e) => {
        const k = e.kind || "outreach";
        return k !== "travel" && k !== "off" && k !== "repair" && k !== "admin";
      }),
    [entries],
  );
  const travelAll = useMemo(() => entries.filter((e) => e.kind === "travel"), [entries]);
  const offAll = useMemo(() => entries.filter((e) => e.kind === "off"), [entries]);
  const repairAll = useMemo(() => entries.filter((e) => e.kind === "repair"), [entries]);
  const adminAll = useMemo(() => entries.filter((e) => e.kind === "admin"), [entries]);

  const inMonth = useCallback((ymd) => {
    const [y, m] = month.split("-").map(Number);
    const first = `${y}-${String(m).padStart(2, "0")}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return ymd >= first && ymd <= last;
  }, [month]);

  const outreach = useMemo(
    () => outreachAll.filter((e) => inMonth(e.date)).sort((a, b) => a.date.localeCompare(b.date)),
    [outreachAll, inMonth],
  );
  const travel = useMemo(
    () => travelAll.filter((e) => inMonth(e.date)).sort((a, b) => a.date.localeCompare(b.date)),
    [travelAll, inMonth],
  );
  const off = useMemo(
    () => offAll.filter((e) => inMonth(e.date)).sort((a, b) => a.date.localeCompare(b.date)),
    [offAll, inMonth],
  );
  const repair = useMemo(
    () => repairAll.filter((e) => inMonth(e.date)).sort((a, b) => a.date.localeCompare(b.date)),
    [repairAll, inMonth],
  );
  const admin = useMemo(
    () => adminAll.filter((e) => inMonth(e.date)).sort((a, b) => a.date.localeCompare(b.date)),
    [adminAll, inMonth],
  );
  // Full-fetch ymd sets — feed the mini-calendar so spillover cells get
  // coloured too.
  const outreachYmds = useMemo(() => new Set(outreachAll.map((e) => e.date)), [outreachAll]);
  const travelYmds = useMemo(() => new Set(travelAll.map((e) => e.date)), [travelAll]);
  const offYmds = useMemo(() => new Set(offAll.map((e) => e.date)), [offAll]);
  const repairYmds = useMemo(() => new Set(repairAll.map((e) => e.date)), [repairAll]);
  const adminYmds = useMemo(() => new Set(adminAll.map((e) => e.date)), [adminAll]);
  const cells = useMemo(() => buildCalendarGrid(month), [month]);

  const shortName = staff.split(" ")[0];

  const scrollToTop = () => {
    // Bring the top of the report into view whenever the user changes
    // staff, so they always see the new header first. (Month flips don't
    // do this — the user is tapping the arrows at the bottom and the page
    // must stay put.)
    const el = previewRef.current;
    if (!el) return;
    let node = el.parentElement;
    while (node) {
      const styles = window.getComputedStyle(node);
      if (styles.overflowY === "auto" || styles.overflowY === "scroll") {
        node.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      node = node.parentElement;
    }
    el.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  const pickStaff = (fullName) => { setStaff(fullName); scrollToTop(); };
  const pickMonth = (nextMonth) => { setMonth(nextMonth); };

  // When the dialog opens, push a history marker so the phone's Back
  // button (or Safari's swipe-back) closes the dialog instead of leaving
  // the Scheduling page. On close we pop the marker back off.
  useEffect(() => {
    if (!open) return;
    const marker = { __hepcureSchedulePreview: true };
    try {
      window.history.pushState(marker, "");
    } catch { /* ignore */ }
    const onPop = () => onOpenChange && onOpenChange(false);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Only unwind our marker if it's still on top.
      try {
        if (window.history.state && window.history.state.__hepcureSchedulePreview) {
          window.history.back();
        }
      } catch { /* ignore */ }
    };
  }, [open, onOpenChange]);

  // After render we present the finished SVG *inside* the dialog with a
  // live "Save SVG" link. Two reasons:
  //   1. iOS Safari drops the user gesture after any `await`, so
  //      programmatic `a.click()` gets blocked silently. Rendering an
  //      <a href={blobUrl}> that the user physically taps IS a fresh
  //      gesture and works on every iOS build we've tested.
  //   2. It lets the user visually confirm the file before saving.
  const [renderedSvg, setRenderedSvg] = useState(null); // { url, filename }
  const svgUrlRef = useRef(null);

  useEffect(() => {
    // Free the blob URL when the dialog closes / we re-render.
    return () => {
      if (svgUrlRef.current) {
        URL.revokeObjectURL(svgUrlRef.current);
        svgUrlRef.current = null;
      }
    };
  }, []);

  // Clear any previously rendered SVG whenever the source changes so the
  // user never saves a stale schedule.
  useEffect(() => {
    if (svgUrlRef.current) {
      URL.revokeObjectURL(svgUrlRef.current);
      svgUrlRef.current = null;
    }
    setRenderedSvg(null);
  }, [staff, month, entries, monthCompleted]);

  const download = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    const src = previewRef.current;

    // Hide any floating toasts / notifications so they don't leak into
    // the rasterised output.
    const floaters = Array.from(document.querySelectorAll(
      "[data-sonner-toaster], [data-sonner-toast], [data-radix-toast-viewport]"
    ));
    const prevFloatDisplay = floaters.map((el) => el.style.display);
    floaters.forEach((el) => { el.style.display = "none"; });

    try {
      // The on-screen day list is a fixed-height scroller so the month
      // arrows never move — expand it to full height for the capture so
      // the exported schedule shows EVERY day card.
      const dayList = src.querySelector('[data-testid="share-schedule-day-list"]');
      const prevListStyle = dayList ? { height: dayList.style.height, overflowY: dayList.style.overflowY } : null;
      if (dayList) { dayList.style.height = "auto"; dayList.style.overflowY = "visible"; }

      // Capture the schedule at its natural width (no widening). Safari
      // will render the SVG in "meet" fit mode — a tall schedule ends up
      // centered with whitespace on the sides on iPhone. The user has
      // explicitly confirmed this layout is preferred.
      let dataUrl;
      try {
        dataUrl = await toSvg(src, {
          cacheBust: true,
          backgroundColor: BG,
          skipFonts: true,
        });
      } finally {
        if (dayList && prevListStyle) {
          dayList.style.height = prevListStyle.height;
          dayList.style.overflowY = prevListStyle.overflowY;
        }
      }
      if (!dataUrl || !String(dataUrl).startsWith("data:image/svg")) {
        throw new Error("Empty SVG payload");
      }

      // Post-process: for iOS Safari's STANDALONE SVG viewer, `html,body`
      // selectors don't apply — the <svg> IS the document root. The
      // correct selector is `:root` (or `svg`), which the senior agent
      // confirmed via RCA. We also embed a native <rect> as a fallback
      // in case CSS is ignored entirely (some builds treat SVGs as
      // opaque images).
      const rawSvg = await (await fetch(dataUrl)).text();
      const openTagMatch = rawSvg.match(/<svg[^>]*>/i);
      let responsiveSvg = rawSvg;
      if (openTagMatch) {
        const openTag = openTagMatch[0];
        const vbMatch = openTag.match(/\bviewBox=(["'])([^"']+)\1/i);
        let vbW = 640;
        let vbH = 1645;
        if (vbMatch) {
          const parts = vbMatch[2].split(/[\s,]+/).map(Number);
          if (parts.length === 4) {
            vbW = parts[2];
            vbH = parts[3];
          }
        } else {
          const wMatch = openTag.match(/\bwidth=(["'])([\d.]+)\1/i);
          const hMatch = openTag.match(/\bheight=(["'])([\d.]+)\1/i);
          if (wMatch) vbW = parseFloat(wMatch[2]);
          if (hMatch) vbH = parseFloat(hMatch[2]);
        }
        const bgRect = `<rect x="0" y="0" width="${vbW}" height="${vbH}" fill="${BG}"/>`;
        // :root targets the SVG element itself in standalone SVG docs.
        // We only paint its background — never touch its dimensions,
        // otherwise the internal calendar/layout distorts.
        const styleBlock =
          `<style><![CDATA[` +
          `:root{background:${BG};}` +
          `svg{background:${BG};display:block;margin:0 auto;}` +
          `]]></style>`;
        responsiveSvg = rawSvg.replace(openTag, openTag + styleBlock + bgRect);
      }

      const filename = `hepcure-${shortName.toLowerCase()}-${month}.svg`;
      const blob = new Blob([responsiveSvg], { type: "image/svg+xml;charset=utf-8" });
      if (svgUrlRef.current) URL.revokeObjectURL(svgUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      svgUrlRef.current = objectUrl;

      // Also fire a silent programmatic download for desktop browsers.
      // If Safari blocks it, no big deal — the user has the visible
      // "Save SVG" link right in front of them.
      try {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch { /* ignore — iOS fallback link handles it */ }

      setRenderedSvg({ url: objectUrl, filename });
      toast.success("Schedule ready — tap the Save link below.");
    } catch (err) {
      toast.error(`Couldn't render SVG: ${err?.message || "unknown error"}`);
      console.error("Share schedule SVG render failed:", err);
    } finally {
      floaters.forEach((el, i) => { el.style.display = prevFloatDisplay[i]; });
      setDownloading(false);
    }
  };

  const renderTime = (e) => {
    if (!e.windows || !e.windows.length) return null;
    return e.windows.map((w) => `${prettyTime(w.start_time)} – ${prettyTime(w.end_time)}`).join(" · ");
  };

  // Combined chronological list — one entry per day, rendered identically
  // to the main OutreachPanel entry card so the exported schedule matches
  // the app view line for line. Filtered to the currently viewed month so
  // the ±7-day fetch buffer (used by the mini-calendar for spillover
  // colouring) doesn't leak into the visible list.
  const allEntries = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = `${y}-${String(m).padStart(2, "0")}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return [...entries]
      .filter((e) => e.date >= first && e.date <= last)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [entries, month]);

  const entryHours = (e) => {
    const totalMins = (e.windows || []).reduce((s, w) => {
      try {
        const [sh, sm] = (w.start_time || "0:0").split(":").map(Number);
        const [eh, em] = (w.end_time   || "0:0").split(":").map(Number);
        return s + Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
      } catch { return s; }
    }, 0);
    if (!totalMins) return null;
    const hrs = totalMins / 60;
    return `${hrs.toFixed(1)} h`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[720px] w-[calc(100vw-2rem)] p-4 sm:p-6"
        style={{ maxHeight: "90vh", overflowY: "auto", overflowX: "hidden" }}
      >
        <DialogHeader>
          <DialogTitle>Share schedule</DialogTitle>
        </DialogHeader>

        {/* Live preview */}
        <div
          ref={previewRef}
          data-testid="share-schedule-preview"
          className="schedule-preview-body"
          style={{
            width: "100%",
            maxWidth: 640,
            background: BG,
            borderRadius: 20,
            padding: 16,
            fontFamily: "'IBM Plex Sans','Inter',system-ui,-apple-system,'Segoe UI',Arial,sans-serif",
            color: TEXT,
            margin: "0 auto",
            boxSizing: "border-box",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {/* Brand row: month in the top-right corner ONLY (no name) */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 14,
          }}>
            <div style={{
              color: GREEN, fontWeight: 700,
              fontFamily: "'Work Sans','Inter',system-ui,sans-serif",
              fontSize: 18, letterSpacing: "0.02em",
            }}>HepCURE</div>
            <div style={{
              fontSize: 11, color: MUTED,
              letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700,
            }}>
              {monthLabelShort}
            </div>
          </div>

          {/* Title card — staff name */}
          <div style={{
            background: "white", border: `1px solid ${BORDER}`, borderRadius: 14,
            padding: 16, marginBottom: 12,
          }}>
            <p style={{
              fontSize: 11, fontWeight: 700, letterSpacing: "0.24em",
              textTransform: "uppercase", color: GREEN, margin: 0,
            }}>Outreach Schedule</p>
            <h1 style={{
              fontFamily: "'Work Sans','Inter',system-ui,sans-serif",
              fontSize: 24, letterSpacing: "-0.01em", margin: "2px 0 4px 0", fontWeight: 700,
            }}>
              {staff}
            </h1>
            <p style={{ color: MUTED, fontSize: 13, margin: 0 }}>
              Confirmed dates for {monthLabel}
            </p>
          </div>

          {/* Calendar card */}
          <div style={{
            background: "white", border: `1px solid ${BORDER}`, borderRadius: 14,
            padding: 16, marginBottom: 12,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h2 style={{
                fontFamily: "'Work Sans','Inter',system-ui,sans-serif",
                fontSize: 16, margin: 0, fontWeight: 700,
              }}>
                {monthLabel}
              </h2>
            </div>

            {/* Calendar table — using <table> (not grid/flex) because iOS
                Safari's standalone SVG image viewer ignores CSS grid/flex
                inside <foreignObject>. Tables render reliably.
                Max-width caps the calendar so columns stay compact even
                when the whole schedule is captured wide (up to ~835px) to
                force iPhone Safari into fit-to-width mode. Without the
                cap, columns balloon and day pills look oversized. */}
            <table style={{
              width: "100%", maxWidth: 460, margin: "0 auto",
              borderCollapse: "separate", borderSpacing: 4,
              tableLayout: "fixed",
            }}>
              <thead>
                <tr>
                  {["S","M","T","W","T","F","S"].map((d, i) => (
                    <th key={i} style={{
                      textAlign: "center", color: MUTED, fontWeight: 700,
                      letterSpacing: "0.06em", textTransform: "uppercase",
                      padding: "4px 0", fontSize: 10.5,
                    }}>{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 6 }).map((_, rowIdx) => (
                  <tr key={rowIdx}>
                    {cells.slice(rowIdx * 7, rowIdx * 7 + 7).map((c) => {
                      // Spillover days (from the previous/next month)
                      // still receive their real category colour so the
                      // calendar looks consistent with the main app.
                      const isOut = outreachYmds.has(c.ymd);
                      const isTrv = travelYmds.has(c.ymd);
                      const isOff = offYmds.has(c.ymd);
                      const isRep = repairYmds.has(c.ymd);
                      const isAdm = adminYmds.has(c.ymd);
                      let bg = "transparent";
                      let color = c.inMonth ? TEXT : "#B4BDB8";
                      let fontWeight = 500;
                      if (isOut) { bg = GREEN; color = "white"; fontWeight = 700; }
                      if (isTrv) { bg = BLUE;  color = "white"; fontWeight = 700; }
                      if (isOff) { bg = RED;   color = "white"; fontWeight = 700; }
                      if (isRep) { bg = AMBER; color = "white"; fontWeight = 700; }
                      if (isAdm) { bg = SLATE; color = "white"; fontWeight = 700; }
                      return (
                        <td key={c.ymd} style={{
                          padding: 0,
                          verticalAlign: "middle",
                        }}>
                          {/* Pill fills the column (width 100%) and stays
                              square via padding-top:100%. Because the
                              table above is capped at maxWidth 460px,
                              columns are ≤ 65px wide, so pills never grow
                              oversized regardless of the outer capture
                              width. */}
                          <div style={{
                            position: "relative",
                            width: "100%",
                            paddingTop: "100%",
                            borderRadius: 8,
                            background: bg,
                          }}>
                            <div style={{
                              position: "absolute", inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color, fontWeight,
                              fontSize: 13,
                              lineHeight: 1,
                            }}>
                              {c.date.getDate()}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Legend — reserve two rows' height so months with Repair/Admin
                entries (second legend row) don't shift everything below. */}
            <div style={{
              display: "flex", gap: 12, fontSize: 11, color: MUTED,
              marginTop: 10, justifyContent: "center", flexWrap: "wrap",
              minHeight: 42, alignContent: "start",
            }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: GREEN }} />
                Outreach · {outreach.length}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: BLUE }} />
                Travel · {travel.length}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: RED }} />
                Off · {off.length}
              </span>
              {repair.length > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: AMBER }} />
                  Repair · {repair.length}
                </span>
              )}
              {admin.length > 0 && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: SLATE }} />
                  Admin · {admin.length}
                </span>
              )}
            </div>
          </div>

          {/* Chronological unified list — identical layout to the main
              OutreachPanel entry cards (date on top, times stacked below
              with inline category chips, total hours + kind badge on the
              right). Grouping into per-kind sections used to force the
              user to visually reconcile two different formats; this
              matches the app view line-for-line. */}
          {/* Fixed-height day-list area — the month arrows below must stay
              glued in place no matter how many day cards a month has.
              Months with more cards scroll INSIDE this box. The SVG export
              expands it back to full height before capturing. */}
          <div
            data-testid="share-schedule-day-list"
            style={{ height: 248, overflowY: "auto", marginBottom: 12 }}
          >
          {allEntries.length > 0 && (
            <div style={{
              background: "white", border: `1px solid ${BORDER}`, borderRadius: 14,
              padding: 10,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {allEntries.map((e) => {
                const kind = (e.kind || "outreach").toLowerCase();
                const hoursLabel = entryHours(e);
                const KIND_COLOR = { outreach: GREEN, travel: BLUE, off: RED, repair: AMBER, admin: SLATE };
                const KIND_LABEL = { outreach: "Work", travel: "Travel", off: "Off", repair: "Repair", admin: "Admin" };
                const KIND_ICON  = { outreach: MapPin, travel: Plane, off: CoffeeIcon, repair: Wrench, admin: Wrench };
                const dayColor = KIND_COLOR[kind] || GREEN;
                const dayLabel = KIND_LABEL[kind] || "Work";
                const DayIcon = KIND_ICON[kind] || MapPin;
                const showBadge = kind !== "outreach";
                return (
                  <div
                    key={e.date}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10, border: `1px solid ${BORDER}`, background: "white",
                    }}
                  >
                    {/* Date + total hours on ONE line */}
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: TEXT, minWidth: 0 }}>
                        {format(fromYMD(e.date), "EEE, MMM d, yyyy")}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                        {hoursLabel && (
                          <span style={{
                            fontSize: 12, fontWeight: 700, color: TEXT,
                            fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                          }}>
                            {hoursLabel}
                          </span>
                        )}
                        <CalendarDays style={{ width: 13, height: 13, color: MUTED, flexShrink: 0 }} />
                      </div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      {kind === "outreach" && e.city && (
                        <div style={{ fontSize: 12, color: MUTED, marginTop: 2, fontWeight: 600 }}>
                          {e.city}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                        {(e.windows && e.windows.length) ? e.windows.map((w, i) => {
                          const wt = (w.work_type || "outreach").toLowerCase();
                          const WT_LABEL = { outreach: "Work", admin: "Admin", driving: "Driving", other: "Other" };
                          const WT_COLOR = { outreach: GREEN, admin: "#8A5A00", driving: BLUE, other: "#6B7A73" };
                          const WT_ICON  = { outreach: MapPin, admin: Wrench, driving: Plane, other: MapPin };
                          // Custom types come from the shared server list
                          // passed in by the panel (e.g. "BD").
                          const custom = workTypes.find((t) => t.key === wt);
                          const label = custom?.label || WT_LABEL[wt] || wt;
                          const color = WT_COLOR[wt] || "#6B7A73";
                          const Icon = WT_ICON[wt] || MapPin;
                          const showChip = kind === "outreach";
                          return (
                            <div key={i} style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 6, minWidth: 0 }}>
                              <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                {prettyTime(w.start_time)} – {prettyTime(w.end_time)}
                              </span>
                              {showChip && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 3,
                                  padding: "1px 5px", borderRadius: 999,
                                  background: `${color}24`, color,
                                  fontSize: 9, fontWeight: 700, letterSpacing: "0.03em",
                                  textTransform: "uppercase",
                                  whiteSpace: "nowrap", flexShrink: 0,
                                }}>
                                  <Icon style={{ width: 8, height: 8 }} strokeWidth={2.5} />
                                  {label}
                                </span>
                              )}
                            </div>
                          );
                        }) : (
                          // No windows on this day (e.g. Off / Repair /
                          // Admin days that were saved without hours).
                          // Render the fallback text ("All day" or
                          // "Hours TBD") inline with the kind chip so
                          // the badge sits in the same visual slot as
                          // the per-slot category chips on Work days.
                          <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 6, minWidth: 0 }}>
                            <em style={{
                              color: kind === "off" ? MUTED : "#B4351B",
                              fontStyle: "italic", whiteSpace: "nowrap",
                            }}>
                              {kind === "off" ? "All day" : "Hours TBD"}
                            </em>
                            {showBadge && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 3,
                                padding: "1px 5px", borderRadius: 999,
                                background: `${dayColor}24`, color: dayColor,
                                fontSize: 9, fontWeight: 700, letterSpacing: "0.03em",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap", flexShrink: 0,
                              }}>
                                <DayIcon style={{ width: 8, height: 8 }} strokeWidth={2.5} />
                                {dayLabel}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {allEntries.length === 0 && !loading && (
            <div style={{
              background: "white", border: `1px solid ${BORDER}`, borderRadius: 14,
              padding: 24, textAlign: "center", color: MUTED, fontSize: 13,
              height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              No days scheduled for {monthLabel} yet.
            </div>
          )}
          </div>
        </div>

        {/* Bottom toolbar — subtle staff switcher + month arrows */}
        <div style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: `1px solid ${BORDER}`,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {/* Staff switcher — staff-scoped logins see ONLY their own pill;
              admin sees the live registry list (new staff included), not a
              frozen list. */}
          {(() => {
            const visibleStaff = lockStaff
              ? [{ full: lockStaff, short: lockStaff.split(" ")[0] }]
              // Staff hidden before their start month — they didn't exist
              // in the month being previewed.
              : (staffList && staffList.length ? staffList : STAFF)
                  .filter((s) => !s.start_month || s.start_month <= month);
            return (
          <div style={{
            display: "grid",
            gridTemplateColumns: visibleStaff.length === 1 ? "1fr" : "repeat(4, 1fr)",
            gap: 4,
          }}>
            {visibleStaff.map((s) => (
              <button
                key={s.full}
                type="button"
                onClick={() => { if (!lockStaff) pickStaff(s.full); }}
                data-testid={`share-schedule-staff-${s.short.toLowerCase()}`}
                style={{
                  padding: "6px 4px",
                  borderRadius: 8,
                  border: `1px solid ${staff === s.full ? GREEN : BORDER}`,
                  background: staff === s.full ? GREEN : "white",
                  color: staff === s.full ? "white" : MUTED,
                  fontSize: 11, fontWeight: 600, cursor: lockStaff ? "default" : "pointer",
                  fontFamily: "inherit",
                  minWidth: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {s.short}
              </button>
            ))}
          </div>
            );
          })()}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => pickMonth(monthShift(month, -1))}
              data-testid="share-schedule-prev-month"
              style={{
                border: `1px solid ${BORDER}`, background: "white", borderRadius: 8,
                padding: "5px 8px", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center",
              }}
              aria-label="Previous month"
            >
              <ChevronLeft style={{ width: 13, height: 13 }} />
            </button>
            <div style={{
              flex: 1, minWidth: 0, textAlign: "center",
              fontWeight: 600, fontSize: 12.5,
              padding: "5px 8px",
              background: "white", border: `1px solid ${BORDER}`, borderRadius: 8,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              color: TEXT,
            }}>
              {monthLabel}
            </div>
            <button
              type="button"
              onClick={() => pickMonth(monthShift(month, 1))}
              data-testid="share-schedule-next-month"
              style={{
                border: `1px solid ${BORDER}`, background: "white", borderRadius: 8,
                padding: "5px 8px", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center",
              }}
              aria-label="Next month"
            >
              <ChevronRight style={{ width: 13, height: 13 }} />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {renderedSvg && (
            <div
              data-testid="share-schedule-save-link-row"
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 10, padding: "10px 12px",
                background: GREEN_SOFT, border: `1px solid ${GREEN}`, borderRadius: 10,
                fontSize: 13, color: TEXT,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>Ready.</strong> Tap the link to save the SVG.
              </span>
              <a
                href={renderedSvg.url}
                download={renderedSvg.filename}
                data-testid="share-schedule-save-link"
                style={{
                  background: GREEN, color: "white",
                  padding: "8px 14px", borderRadius: 8,
                  fontWeight: 700, textDecoration: "none",
                  whiteSpace: "nowrap", flexShrink: 0,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Download style={{ width: 14, height: 14 }} />
                Save SVG
              </a>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button
              onClick={download}
              disabled={downloading || loading}
              data-testid="share-schedule-download"
              style={{ background: GREEN, color: "white" }}
            >
              <Download style={{ width: 14, height: 14, marginRight: 6 }} />
              {downloading ? "Rendering…" : (renderedSvg ? "Re-render SVG" : "Download SVG")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
